import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { timeLeftLabel } from "@/lib/format";
import { Gift, Ticket, Repeat, Lock, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

export function AccessStatus({ access, suppressSelfCta = false }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (!access) return null;

  let state;
  if (access.subscription_active) {
    state = {
      icon: Repeat,
      title: "Monthly Subscription Active",
      desc: "Unlimited communication analyses this cycle.",
      tone: "cyan",
      cta: null,
    };
  } else if (access.free_available) {
    const left = timeLeftLabel(access.free_expires_at);
    state = {
      icon: Gift,
      title: "Complimentary Analysis Available",
      desc: left ? `Use it within your 24-hour window · ${left}` : "Use it within your 24-hour window.",
      tone: "cyan",
      cta: { to: "/upload", label: "Start free analysis" },
      countdown: true,
    };
  } else if (access.credits > 0) {
    state = {
      icon: Ticket,
      title: `${access.credits} Single Analysis Credit${access.credits > 1 ? "s" : ""}`,
      desc: "Ready to use whenever you upload audio.",
      tone: "blue",
      cta: { to: "/upload", label: "Start analysis" },
    };
  } else if (access.free_expired) {
    state = {
      icon: Lock,
      title: "Complimentary Window Expired",
      desc: "Your free analysis was not used within 24 hours. Purchase a single analysis or subscribe to continue.",
      tone: "locked",
      cta: { to: "/billing", label: "View plans" },
    };
  } else {
    state = {
      icon: Lock,
      title: "No Active Access",
      desc: "Purchase a single analysis or subscribe monthly to run a report.",
      tone: "locked",
      cta: { to: "/billing", label: "View plans" },
    };
  }

  const Icon = state.icon;
  const toneClasses = {
    cyan: "border-cyan-400/40 bg-cyan-400/[0.06]",
    blue: "border-blue-500/40 bg-blue-500/[0.06]",
    locked: "border-slate-700 bg-slate-900/40",
  }[state.tone];
  const iconTone = {
    cyan: "text-cyan-300 bg-cyan-400/10 border-cyan-400/40",
    blue: "text-blue-300 bg-blue-500/10 border-blue-500/40",
    locked: "text-slate-400 bg-slate-800 border-slate-700",
  }[state.tone];

  return (
    <div
      data-testid="access-status"
      className={`rounded-xl border p-6 ${toneClasses}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${iconTone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-white" data-testid="access-title">
              {state.title}
            </h3>
            {state.countdown && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-950/60 px-2 py-0.5 text-xs font-mono text-cyan-300 border border-cyan-400/30">
                <Clock className="h-3 w-3" />
                {timeLeftLabel(access.free_expires_at) || "expiring"}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-400">{state.desc}</p>
        </div>
        {state.cta && !(suppressSelfCta && state.cta.to === "/upload") && (
          <Button
            asChild
            data-testid="access-cta"
            className={
              (state.tone === "locked"
                ? "bg-slate-800 text-white hover:bg-slate-700"
                : "bg-cyan-400 text-black font-semibold hover:bg-cyan-300") +
              " w-full sm:w-auto sm:shrink-0"
            }
          >
            <Link to={state.cta.to}>{state.cta.label}</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
